import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/apiClient";
import { Activity, Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

const VALIDITY = {
  valid: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", label: "Valid Prescription" },
  cancelled: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", label: "Cancelled" },
  expired: { icon: XCircle, color: "text-amber-600", bg: "bg-amber-50", label: "Expired" },
  dispensed: { icon: CheckCircle2, color: "text-slate-600", bg: "bg-slate-100", label: "Already Dispensed" },
};

export default function PrescriptionVerify() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { axios.get(`${API}/public/prescriptions/verify/${token}`).then((r) => setData(r.data)).finally(() => setLoading(false)); }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  const notFound = !data?.found;
  const v = VALIDITY[data?.validity] || VALIDITY.valid;
  const Icon = notFound ? XCircle : v.icon;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-blue-600 text-white px-6 py-5">
        <div className="max-w-2xl mx-auto flex items-center gap-2"><Activity className="w-6 h-6" strokeWidth={2.5} /><span className="text-xl font-bold font-head">ReferU</span><span className="text-sm opacity-80 ml-2">Prescription Verification</span></div>
      </header>
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center" data-testid="verify-result">
          {notFound ? (
            <>
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4"><XCircle className="w-8 h-8 text-red-600" /></div>
              <h1 className="text-2xl font-semibold text-slate-900 font-head">Not Found</h1>
              <p className="text-slate-500 mt-2">No prescription matches this code. Please check with the prescriber.</p>
            </>
          ) : (
            <>
              <div className={`w-16 h-16 rounded-2xl ${v.bg} flex items-center justify-center mx-auto mb-4`}><Icon className={`w-8 h-8 ${v.color}`} /></div>
              <h1 className="text-2xl font-semibold text-slate-900 font-head">{v.label}</h1>
              <div className="mt-6 text-left divide-y divide-slate-100">
                <Row k="Prescription ID" val={data.prescription_number} />
                <Row k="Status" val={data.status} />
                <Row k="Issued" val={data.issued_date} />
                <Row k="Prescriber" val={`${data.doctor} (${data.doctor_reg || "—"})`} />
                <Row k="Patient" val={data.patient_initials} />
                <Row k="Medications" val={`${data.medication_count} item(s)`} />
              </div>
            </>
          )}
        </div>
        <p className="text-center text-xs text-slate-400 mt-6 flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Verified securely by ReferU by Scrilcon · Patient details are protected</p>
      </div>
    </div>
  );
}

function Row({ k, val }) {
  return <div className="flex justify-between py-2.5 text-sm"><span className="text-slate-400">{k}</span><span className="font-medium text-slate-800 capitalize">{val}</span></div>;
}
